import CopyFromLayerButton from "./CopyFromLayerButton";
import * as support from "./support";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import * as arrays from "common/arrays";
import * as ids from "common/ids";
import Component from "components/Component";
import IconButton from "components/common/IconButton";
import TextField from "components/common/TextField";
import * as labels from "config.labels";
import { useCallback, useEffect, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import * as endorsementsStore from "store/endorsements";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as userSelectors from "store/selectors/user/userSelectors";

const DND_TYPE_SUBJECTIVITIES = "SUBJECTIVITIES";

const Entry = ({ index, value, onUpdate, onDelete, onDrop, editable }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const [, dragRef, dragPreviewRef] = useDrag(() => ({
    type: DND_TYPE_SUBJECTIVITIES,
    item: { index },
  }));

  const [, dropRef] = useDrop(
    () => ({
      accept: DND_TYPE_SUBJECTIVITIES,
      drop: (item) => onDrop(item.index),
    }),
    [onDrop]
  );

  const isCustom = value?.type === "CUSTOM";
  const showTextField = editable || isCustom;

  return (
    <Stack
      direction={"row"}
      spacing={2}
      justifyContent={"flex-start"}
      alignItems={"center"}
      ref={(el) => {
        dragRef(el);
        dropRef(el);
        dragPreviewRef(el);
      }}
    >
      <Typography
        width={20}
        align={"right"}
        fontWeight={"bold"}
        sx={{ padding: 2 }}
      >
        {index + 1}
      </Typography>
      {showTextField ? (
        <TextField
          hiddenLabel
          multiline
          value={value?.text ?? ""}
          onChange={(e) =>
            onUpdate({
              ...value,
              text: e.target.value,
            })
          }
        />
      ) : (
        <Typography sx={{ flex: 1 }}>{value?.text ?? ""}</Typography>
      )}
      <Stack
        direction={"row"}
        spacing={0}
        justifyContent={"flex-start"}
        alignItems={"center"}
      >
        <FormControlLabel
          onChange={() =>
            onUpdate({
              ...value,
              fulfilled: !(value?.fulfilled ?? false),
            })
          }
          control={<Checkbox checked={value?.fulfilled ?? false} />}
          label={translateLabel(labels.SUBJECTIVITIES_FULFILLED_LABEL, {
            default: "Fulfilled?",
          })}
        />
        <IconButton icon={DeleteIcon} onClick={onDelete} />
        <DragIndicatorIcon />
      </Stack>
    </Stack>
  );
};

const SelectedSubjectivities = ({
  entries,
  updateEntry,
  deleteEntry,
  moveEntry,
  editable,
}) => {
  return (
    <Stack direction={"column"} spacing={1}>
      {entries.map((entry, index) => (
        <Entry
          key={index}
          index={index}
          value={entry}
          onUpdate={updateEntry(entry.key)}
          onDelete={deleteEntry(entry.key)}
          onDrop={moveEntry(index)}
          editable={editable}
        />
      ))}
    </Stack>
  );
};

const StandardSubjectivitiesSelector = ({ config, entries, addEntry }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const visibleKeys = (config?.defaultVisibleSubjectivities ?? []).filter(
    (key) => config?.subjectivities?.[key]?.key === key
  );
  const selectedKeys = new Set(entries?.map(({ key }) => key));
  const unselectedKeys = visibleKeys.filter((key) => !selectedKeys.has(key));

  const options = unselectedKeys.map((key) => config?.subjectivities?.[key]);

  return (
    <Autocomplete
      value={null}
      onChange={(_, value, reason) => {
        if (reason === "selectOption") {
          addEntry(value);
        }
      }}
      options={options}
      getOptionKey={({ key }) => key}
      getOptionLabel={({ text }) => text}
      renderInput={(params) => (
        <TextField
          {...params}
          label={translateLabel(labels.SUBJECTIVITIES_STANDARD_LABEL, {
            default: "Standard Subjectivities",
          })}
          variant={"filled"}
          InputLabelProps={{ shrink: true }}
        />
      )}
      autoHighlight
      disableClearable
      blurOnSelect
      fullWidth
    />
  );
};

const AvailableSubjectivities = ({ config, entries, addEntry }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  return (
    <Paper sx={{ width: "100%" }}>
      <Stack
        direction={"column"}
        spacing={2}
        sx={{
          padding: 2,
        }}
      >
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography variant={"subtitle1"}>
            {translateLabel(labels.SUBJECTIVITIES_AVAILABLE_TITLE, {
              default: "Available Subjectivities",
            })}
          </Typography>
        </Stack>
        <StandardSubjectivitiesSelector
          config={config}
          entries={entries}
          addEntry={addEntry}
        />
        <Box>
          <IconButton
            icon={AddIcon}
            onClick={() => addEntry({ text: "" })}
            tooltip={translateLabel(labels.SUBJECTIVITIES_ADD_CUSTOM_TOOLTIP, {
              default: "Add Custom",
            })}
          />
        </Box>
      </Stack>
    </Paper>
  );
};

export const Subjectivities = ({ layerId }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const dispatch = useDispatch();

  const { config } = support.usePolicyConfig({ layerId });

  const userConfig = useSelector(userSelectors.selectUserConfig);
  const subjectivitiesEditable =
    userConfig.componentVisibility?.["<Templates>.subjectivitiesEditable"] ??
    false;

  const rawEntries = useSelector(endorsementsStore.selectInput)
    ?.subjectivitiesByLayer?.[layerId];
  const entries = rawEntries ?? [];

  // Captured synchronously at render time — avoids race with the mount effect
  // that dispatches lock() (Redux update arrives one render later).
  const initiallyHadEntries = useRef(rawEntries != null);

  const locked = useSelector(
    endorsementsStore.selectSubjectivitiesLockedByLayer(layerId)
  );

  const lock = useCallback(
    () => dispatch(endorsementsStore.lockLayerSubjectivities({ layerId })),
    [dispatch, layerId]
  );

  const setEntries = useCallback(
    (newEntries) =>
      dispatch(
        endorsementsStore.setLayerSubjectivities({
          layerId,
          subjectivities: newEntries,
        })
      ),
    [dispatch, layerId]
  );

  /* Lock this layer on mount if it already has saved subjectivities. */
  useEffect(() => {
    if (rawEntries != null) {
      lock();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addEntry = (value) => {
    const entry = value?.key
      ? { ...value }
      : { ...value, type: "CUSTOM", key: ids.makeNewId() };
    lock();
    setEntries([...entries, entry]);
  };

  const updateEntry = (key) => (value) => {
    lock();
    setEntries(
      entries.map((entry) =>
        entry.key === key ? { ...entry, ...value } : entry
      )
    );
  };

  const deleteEntry = (key) => () => {
    lock();
    setEntries(entries.filter((entry) => entry.key !== key));
  };

  const moveEntry = (toIndex) => (fromIndex) => {
    lock();
    setEntries(arrays.move(entries, { fromIndex, toIndex }));
  };

  const appliedDefaultsRef = useRef(null);
  useEffect(() => {
    if (locked || initiallyHadEntries.current) return;
    const defaults = config?.defaultSelectedSubjectivities;
    if (!defaults?.length) return;
    const key = JSON.stringify(defaults);
    if (appliedDefaultsRef.current === key) return;
    appliedDefaultsRef.current = key;
    setEntries(
      defaults.map((k) => config?.subjectivities?.[k]).filter(Boolean)
    );
  }, [config, locked, setEntries]);

  return (
    <Component
      title={translateLabel(labels.SUBJECTIVITIES_TITLE, {
        default: "Subjectivities",
      })}
      subtitle={translateLabel(labels.SUBJECTIVITIES_SUBTITLE, {
        default: "",
      })}
      options={
        <CopyFromLayerButton layerId={layerId} type={"SUBJECTIVITIES"} />
      }
    >
      <Stack direction={"column"} spacing={2}>
        <SelectedSubjectivities
          entries={entries}
          addEntry={addEntry}
          updateEntry={updateEntry}
          deleteEntry={deleteEntry}
          moveEntry={moveEntry}
          editable={subjectivitiesEditable}
        />
        <AvailableSubjectivities
          config={config}
          entries={entries}
          addEntry={addEntry}
        />
      </Stack>
    </Component>
  );
};

export default Subjectivities;
